import dayjs from 'dayjs';

/**
 * Calendar dates vs. instants.
 *
 * The API serializes every date as an instant, so a value meaning "31 July 2026" and a value
 * meaning "the moment this record was created" arrive in the same shape. Rendering a calendar date
 * in a provider timezone is the Mexico off-by-one: 2026-07-31T00:00:00.000Z converted to
 * America/Mexico_City (UTC-6) lands on 2026-07-30 18:00 and renders 30.07.2026.
 *
 * An earlier attempt guessed from the value: treat exact UTC midnight as a calendar date. That is
 * unsound, and not rarely. At UTC-6 every 18:00 local IS exact UTC midnight, so an ordinary 6pm
 * telemedicine appointment -- slots run 07:00-21:00 on the half hour -- was read as a calendar date
 * and shown as "01.08.2026 12:00 AM" instead of "31.07.2026 6:00 PM". A value cannot tell you what
 * it means, and the arithmetic that said otherwise assumed uniformly distributed milliseconds,
 * which scheduling does not obey.
 *
 * What a value CAN tell you is that it carries no time at all. '2026-07-31' has no instant reading
 * -- there is no time of day to convert -- so treating it as a calendar date is always right. That
 * is the only inference made here.
 *
 * Everything else takes its meaning from the FIELD, which a Vue filter never sees. So date-only
 * fields declare themselves at the call site with skipTimezone=true, and scripts/date-check.js
 * enforces that against DATE_ONLY_FIELDS below. The register is the contract and the checker is
 * what makes it hold; neither this module nor filters.js can decide it alone.
 */

// A value with no time component. Anything carrying a time is an instant unless its call site says
// otherwise -- see above for why exact UTC midnight is not a safe signal.
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isDateOnly = (value) => typeof value === 'string' && DATE_ONLY_RE.test(value.trim());

/**
 * Drop a trailing Z so a string is read as a wall clock rather than an instant.
 *
 * For a picker with no value-format, Element falls back to new Date(value), which reads
 * '2026-03-01T00:00:00.000Z' as an instant and paints 28.02.2026 for a UTC-6 operator.
 *
 * Only for values that are genuinely calendar dates. Stripping the zone off a real instant moves
 * it: a claim entered at midnight in Tokyo is stored 2026-01-07T15:00:00.000Z, and stripping makes
 * the picker show 7 January instead of 8. And do NOT use it for a picker that declares a
 * value-format ending in a literal Z -- Element parses against that pattern, the Z is an ordinary
 * character, and a stripped value stops matching and parses to null, blanking the picker.
 */
export const stripZone = (value) => (typeof value === 'string' ? value.replace(/Z$/, '') : value);

// Parse a calendar date without letting a trailing Z pull it into another day.
export const parseDateValue = (value) => dayjs(stripZone(String(value)));

/**
 * The register. scripts/date-check.js reads these two lists, so a field added here is a field the
 * checker starts watching -- keep them as the single source of truth rather than restating the
 * names in the script.
 *
 * Calendar dates. Rendering these must never apply a timezone.
 */
export const DATE_ONLY_FIELDS = [
  'birthDate',
  'birthdate',
  'dueDate',
  'paidDate',
  'startDate',
  'endDate',
  'startAt',
  'endAt',
  'policyStartDate',
  'policyEndDate',
  'insuranceStartDate',
  'insuranceEndDate',
  'visitDate',
  'activityDate',
  'paymentDate',
  'monthlyYear',
  'plannedDisableDate',
  'lastTaskCreated',
  'lastAction',
  'validityFrom',
  'validityTo',
];

/**
 * Real instants. These must keep converting to the provider timezone -- an event that happened at
 * 23:00 UTC did happen on the previous day for a reader in Mexico, and saying so is correct.
 */
export const INSTANT_FIELDS = [
  // Claims are a calendar date the operator picked, stored as provider-local midnight encoded as
  // an instant (InsuranceClaimSaveAction keeps whatever the client sent). Converting that back to
  // the provider zone recovers the intended day, so they are handled as instants, not skipped.
  'claimDate',
  'eventDate',
  'documentReceivedDate',
  'sentToCarrierDate',
  'createdAt',
  'updatedAt',
  'createAt',
  'sentAt',
  'scheduledAt',
  'mergedAt',
  'mergeDateTime',
  'capturedAt',
  'dateTime',
  'created',
];
